'use client';

import { useCallback, useMemo, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';

import Image from 'next/image';
import Link from 'next/link';

import { User } from '@/app/api';

import highlightIcon from '@/public/public/images/navigation-highlight.png';
import MenuBurger from '@/app/components/MenuBurger/MenuBurger';
import ChevronIcon from '@/app/components/ChevronIcon/ChevronIcon';
import Tooltip from '@/app/components/Tooltip/Tooltip';
import DrawerNavigation from '@/app/components/DrawerNavigation/DrawerNavigation';
import { countryConfig } from '@/app/utils/countryConfig';

export type DropDownItem = {
  titleTranslationKey: string;
  path?: string;
  onClick?: () => void;
};

export interface MenuItem {
  titleTranslationKey: string;
  descriptionTranslationKey?: string;
  path?: string;
  onClick?: () => void;
  isHighlighted?: boolean;
  dropdownItems?: DropDownItem[];
}

interface MainNavigationProps {
  currentUser?: User;
}

const DesktopNavLinks = ({ menuItems, className }: { menuItems: MenuItem[]; className?: string }) => {
  const { t } = useTranslation();
  return (
    <>
      <ul className={className}>
        {menuItems.map((menuItem) => (
          <li key={menuItem.titleTranslationKey} className="relative">
            {menuItem.isHighlighted && (
              <Image
                src={highlightIcon}
                alt={t('components.MainNavigation.highlight')}
                className="absolute w-[18px] h-[18px] left-[50%] ml-[3px] top-[-8px]"
              />
            )}
            <Link
              className="group py-[8px] ml-[24px] hover:text-primary-mid flex items-center !opacity-100 !visible"
              href={menuItem.path ?? '#'}
              data-tooltip-id={menuItem.dropdownItems && 'dropdown-menu'}
              prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
              data-tooltip-class-name="top-[80px]"
              data-tooltip-html={
                menuItem.dropdownItems &&
                renderToString(
                  <div>
                    {!!menuItem.descriptionTranslationKey && (
                      <div className="mb-[8px] text-neutral-mid text-[14px]">
                        {t(menuItem.descriptionTranslationKey)}
                      </div>
                    )}
                    <ul>
                      {menuItem.dropdownItems.map((dropdownItem, index) => (
                        <li key={index}>
                          {!!dropdownItem.path && (
                            <Link
                              prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
                              href={dropdownItem.path}
                              className="block py-[8px] hover:text-primary-mid text-[16px]"
                            >
                              {t(dropdownItem.titleTranslationKey)}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>,
                )
              }
            >
              <span>{t(menuItem.titleTranslationKey)}</span>
              {!!menuItem.dropdownItems && (
                <ChevronIcon className="ml-[8px] h-[14px] w-[14px] group-hover:rotate-180" />
              )}
            </Link>
          </li>
        ))}
      </ul>
      <Tooltip id="dropdown-menu" place="bottom" className="!w-[300px] shadow-sm" />
    </>
  );
};

const MainNavigation = ({ currentUser }: MainNavigationProps) => {
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(false);

  // TODO Temporary workaround
  const prefixMenuItemsPaths = useCallback((items: MenuItem[]): MenuItem[] => {
    const baseUrl = process.env.NEXT_PUBLIC_REDIRECT_URL;

    return items.map((item) => {
      const prefixed = {
        ...item,
        path: item.path ? (baseUrl?.endsWith('/') ? `${baseUrl}${item.path}` : `${baseUrl}/${item.path}`) : item.path,
      };

      if (!prefixed.dropdownItems) {
        return prefixed;
      }

      const dropdownItemsPrefixed = prefixMenuItemsPaths(prefixed.dropdownItems);

      return {
        ...prefixed,
        dropdownItems: dropdownItemsPrefixed,
      };
    });
  }, []);

  const prefixedMenuItems = useMemo(
    () => prefixMenuItemsPaths(countryConfig.mainNavigationItems),
    [countryConfig.mainNavigationItems, prefixMenuItemsPaths],
  );

  const handleToggleNavDrawer = useCallback(() => {
    setIsNavDrawerOpen((current) => !current);
  }, [setIsNavDrawerOpen]);

  return (
    <nav className="flex items-center justify-end">
      <DesktopNavLinks className="hidden xl:flex" menuItems={prefixedMenuItems} />
      <MenuBurger isOpen={isNavDrawerOpen} onClick={handleToggleNavDrawer} className="block xl:hidden" />
      <DrawerNavigation menuItems={prefixedMenuItems} isOpen={isNavDrawerOpen} currentUser={currentUser} />
    </nav>
  );
};

export default MainNavigation;
